import PropTypes from "prop-types";
import React, {useMemo, useRef} from "react";
import {useCountries} from "../../hooks/useCountries";

const priorityCountryCodes = ['CH', 'DE', 'FR', 'AT', 'IT', 'LI'];

const CountrySelector = ({ 
  selectedCountry,
  onCountryChange,
  t,
  apiLanguageCode,
  className
}) => {
  const countrySelectRef = useRef(null);
  const {data: countryData = [], isLoading: isLoadingCountries} = useCountries();

  const countries = useMemo(() => {
    return countryData
      .map(country => {
        const translations = country.translations || {};
        let localizedName;

        if (translations[apiLanguageCode]) {
          localizedName = translations[apiLanguageCode].common;
        }
        else if (translations[convertLanguageCode(apiLanguageCode, 'two-letter')]) {
          localizedName = translations[convertLanguageCode(apiLanguageCode, 'two-letter')].common;
        }
        else {
          localizedName = country.name.common;
        }

        return {
          name: localizedName,
          code: country.cca2,
          isPriority: priorityCountryCodes.includes(country.cca2)
        };
      })
      .sort((a, b) => {
        if (a.isPriority && !b.isPriority) return -1;
        if (!a.isPriority && b.isPriority) return 1;

        if (a.isPriority && b.isPriority) {
          return priorityCountryCodes.indexOf(a.code) - priorityCountryCodes.indexOf(b.code);
        }

        return a.name.localeCompare(b.name);
      });
  }, [apiLanguageCode, countryData]);
  
  const handleCountryChange = (e) => {
    const selectedCountryCode = e.target.value;
    const selectedCountry = countries.find(c => c.code === selectedCountryCode);
    
    onCountryChange({
      code: selectedCountryCode,
      name: selectedCountry ? selectedCountry.name : ''
    });
  };
  

  
  return (
    <select
      ref={countrySelectRef}
      className={className}
      onChange={handleCountryChange}
      value={(countries.some(c => c.code === selectedCountry) ? selectedCountry : '') || ''}
      disabled={isLoadingCountries}
    >
      <option disabled value="">
        {isLoadingCountries 
          ? t("ViolatorsAddressModal.loadingCountries") 
          : t("ViolatorsAddressModal.selectCountry")}
      </option>
      
      {countries.map(country => (
        <option 
          key={country.code} 
          value={country.code}
          style={country.isPriority ? { fontWeight: 'bold' } : {}}
        >
          {country.name} ({country.code})
        </option>
      ))}
    </select>
  );
};

CountrySelector.propTypes = {
  selectedCountry: PropTypes.string,
  onCountryChange: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  apiLanguageCode: PropTypes.string.isRequired,
  className: PropTypes.string
};
export const convertLanguageCode = (code, toFormat = 'two-letter') => {
  const twoLetterToThreeLetter = {
    DE: 'deu',
    FR: 'fra',
    IT: 'ita',
    EN: 'eng',
  };
  const threeLetterToTwoLetter = {
    deu: 'DE',
    fra: 'FR',
    ita: 'IT',
    eng: 'EN',
  };

  const normalizedTwoLetterCode = code.toUpperCase();
  const normalizedThreeLetterCode = code.toLowerCase();
  
  if (toFormat === 'two-letter') {
    if (threeLetterToTwoLetter[normalizedThreeLetterCode]) {
      return threeLetterToTwoLetter[normalizedThreeLetterCode];
    }

    if (twoLetterToThreeLetter[normalizedTwoLetterCode]) {
      return normalizedTwoLetterCode;
    }

    return 'EN';
  }

  if (twoLetterToThreeLetter[normalizedTwoLetterCode]) {
    return twoLetterToThreeLetter[normalizedTwoLetterCode];
  }

  if (threeLetterToTwoLetter[normalizedThreeLetterCode]) {
    return normalizedThreeLetterCode;
  }

  return 'eng';
};
export default CountrySelector;
